import type { Theme as GlitzTheme } from '@glitz/core';
import { ThemeProvider as GlitzThemeProvider } from '@glitz/react';
import * as React from 'react';
import {
  a11yGrey,
  black,
  darkGrey,
  green,
  grey,
  orange,
  paleBlack,
  red,
  secondaryGrey,
  shadedGrey,
  sharedFontSize,
  sharedSpacing,
  softWhite,
  tintedBlack,
  white,
} from 'webapp/shared/sharedTheme';

export const secondaryLight = '#ffc166';
export const secondaryBrighter = '#ffe0b3';
export const secondaryBrightest = '#fff0d9';

export const monochrome = '#F3F3F3';
export const monochromeLight = '#ebebeb';
export const monochromeBrighter = '#f5f5f5';
export const creamWhite = '#FFF1D9';

const beigeDark = '#EEE8CF';
const beige = '#FFFAE8';

const darkRed = '#671D1A';
const mainRed = '#8E2B27';

const duskDark = '#6B758C';
const dusk = '#878EA0';

const primary = {
  color: beige,
  hover: beigeDark,
  text: tintedBlack,
  border: beige,
};

const tertiary = {
  color: darkRed,
  hover: mainRed,
  text: white,
  border: darkRed,
};

const fontSize = { xxxl: 56, xxl: 32, xl: 32, l: 28, m: 22, s: 18, xs: 16, xxs: 14, xxxs: 12 };

const fontWeight = {
  bold: 500,
  semibold: 450,
  normal: 400,
  light: 300,
};

export const theme: GlitzTheme = {
  id: 'lightshop',
  primary: primary,
  secondary: {
    color: tintedBlack,
    hover: paleBlack,
    text: white,
    border: tintedBlack,
  },
  tertiary: tertiary,
  quaternary: primary,
  monochrome: {
    color: softWhite,
    hover: grey,
    text: black,
    border: softWhite,
  },
  monochromeBrighter: {
    color: '#f5f5f5',
    hover: '#eeeeee',
    text: black,
    border: '#f5f5f5',
  },
  positive: {
    color: '#4ec791',
    hover: '#36ab76',
    text: white,
    border: '#4ec791',
  },
  negative: {
    color: red,
    hover: red,
    text: white,
    border: red,
  },
  transparent: {
    color: 'transparent',
    hover: 'transparent',
    text: 'inherit',
    border: 'transparent',
  },
  white: {
    color: white,
    hover: softWhite,
    text: tintedBlack,
    border: paleBlack,
    hoverBorder: paleBlack,
  },
  dark: {
    color: shadedGrey,
    hover: darkGrey,
    text: white,
    border: shadedGrey,
  },
  disabled: {
    color: monochromeBrighter,
    hover: monochromeBrighter,
    text: shadedGrey,
    border: monochromeBrighter,
  },
  myPagesMenu: {
    color: monochromeLight,
    fontWeight: fontWeight.semibold,
  },
  myPagesLoyaltyPoints: {
    color: softWhite,
    fontWeight: fontWeight.semibold,
    textTransform: 'uppercase',
  },
  myPagesInfoBox: {
    color: monochromeLight,
  },
  myPagesProfilePage: {
    color: monochromeLight,
    textTransform: 'uppercase',
  },
  notificationColor: {
    color: duskDark,
    text: white,
  },
  productPageInfo: {
    color: beigeDark,
  },
  fontFamily: {
    primary: 'futura',
    secondary: 'futura',
    tertiary: 'futura',
  },
  footer: {
    color: beige,
    fontSize: fontSize.xs,
    heading: {
      fontSize: fontSize.m,
      fontWeight: fontWeight.semibold,
    },
    infoFooter: {
      color: tintedBlack,
      text: shadedGrey,
    },
    accordion: {
      backgroundColor: beige,
      borderColor: beigeDark,
    },
  },
  memberClubNewsletter: {
    color: darkRed,
    fontSize: fontSize.xs,
    text: white,
  },
  quickSearch: {
    borderColor: darkGrey,
    backgroundColor: white,
    borderRadius: 50,
    highlightText: black,
  },
  searchSuggestions: {
    borderRadius: 16,
    position: 40,
    borderColor: secondaryGrey,
    color: a11yGrey,
    backgroundColor: white,
  },
  price: {
    current: { card: { fontWeight: fontWeight.bold }, page: { fontWeight: fontWeight.bold } },
    original: { fontWeight: fontWeight.normal, fontSize: fontSize.xs },
    discount: { color: mainRed },
    vatText: { fontWeight: fontWeight.normal },
  },
  productCard: {
    brand: { fontSize: fontSize.xxs, color: paleBlack, fontWeight: fontWeight.bold },
    title: { fontSize: fontSize.xxs, fontWeight: fontWeight.semibold },
    price: { fontSize: fontSize.xxs },
    wishlistIcon: { color: black },
  },
  header: {
    backgroundColor: beige,
    menuTextTransform: 'initial',
    fontSize: fontSize.s,
    fontWeight: fontWeight.semibold,
    subCategoryFontSize: fontSize.xs,
    menuHeadingFontSize: fontSize.m,
    menuHightlightColor: beigeDark,
    topMenu: { fontWeight: fontWeight.semibold },
  },
  topBanner: {
    color: beigeDark,
    text: 'inherit',
    checkColor: 'inherit',
  },
  fontWeight: {
    bold: fontWeight.bold,
    normal: fontWeight.normal,
    semiBold: fontWeight.semibold,
    primary: fontWeight.semibold,
    secondary: fontWeight.normal,
  },
  heading: {
    fontWeight: fontWeight.semibold,
  },
  headerIndicators: {
    cart: {
      backgroundColor: mainRed,
      color: white,
    },
    favourites: {
      backgroundColor: mainRed,
      color: white,
    },
  },
  actionButton: tertiary,
  filterButton: tertiary,
  linkButton: primary,
  wishlistButton: {
    color: white,
    hover: softWhite,
    text: tintedBlack,
    border: '#c9c0bb',
    hoverBorder: paleBlack,
  },
  carouselButton: {
    color: tintedBlack,
    text: white,
    hover: paleBlack,
    border: tintedBlack,
  },
  secondaryCarouselButton: {
    color: tintedBlack,
    text: white,
    hover: paleBlack,
    border: tintedBlack,
  },
  bubble: { color: tintedBlack, backgroundColor: beigeDark },
  button: { textTransform: 'initial', fontWeight: fontWeight.semibold },
  checkbox: {
    color: darkGrey,
    uncheckedColor: 'transparent',
    checkedColor: 'white',
    disabled: monochrome,
    borderColor: tintedBlack,
    checkedBackgroundColor: tintedBlack,
  },
  sharedSpacing: sharedSpacing,
  spacing: { xxxxl: 70, xxxl: 70, xxl: 70, xl: 40, l: 24, m: 16, s: 12, xs: 8, xxs: 4 },
  sharedFontSize: sharedFontSize,
  fontSize: fontSize,
  radius: {
    primary: 0,
    secondary: 0,
  },
  monochromeLight,
  darkGrey: tintedBlack,
  secondaryBrighter,
  secondaryBrightest: darkRed,
  secondaryLight,
  creamWhite,
  lightBackground: 'white',
  flyout: {
    headerColor: white,
    headerHeight: 100,
    headerTextPosition: 'start',
    backgroundColor: beige,
  },
  borderColor: monochromeLight,
  borderHighlight: tintedBlack,
  ratings: { backgroundColor: monochromeLight, color: secondaryLight },
  borderStrongColor: '#c9c0bb',
  breadcrumbs: {
    fontSizeLargeViewport: fontSize.xxs,
    fontSizeBase: fontSize.xxs,
    segmentFontWeight: 550,
    lastSegmentColor: 'inherit',
    color: 'inherit',
  },
  stockStatus: {
    inStock: green,
    lowInStock: orange,
    outOfStock: red,
    unknown: shadedGrey,
  },
  newsBadge: {
    color: beigeDark,
    text: tintedBlack,
  },
  thumbnailBorder: 'transparent',
  mobileMenu: {
    backgroundColor: beige,
    accentColor: beigeDark,
    borderColor: beigeDark,
    fontSize: fontSize.s,
    fontWeight: fontWeight.normal,
    tabs: {
      fontSize: fontSize.s,
      fontWeight: fontWeight.semibold,
      textTransform: 'initial',
    },
  },
  checkout: {
    backgroundColor: monochromeBrighter,
    text: 'inherit',
  },
  facets: {
    flyoutHeader: { fontSize: fontSize.m },
    heading: { fontSize: fontSize.s, fontWeight: fontWeight.normal },
    facet: { fontSize: fontSize.xs },
    count: { fontSize: fontSize.xxs, color: darkGrey },
    borderColor: grey,
    search: {
      color: white,
      borderColor: shadedGrey,
    },
    range: {
      sliderTrackColor: shadedGrey,
      sliderMarkColor: tintedBlack,
      sliderRailColor: softWhite,
      markDiameter: 16,
      railHeight: 4,
    },
    adornment: {
      input: { color: darkGrey },
      affix: { fontSize: sharedFontSize.xs, color: darkGrey },
    },
  },
  scrollbar: {
    trackColor: softWhite,
    thumbColor: shadedGrey,
    radius: 0,
  },
  imageBackgroundColor: 'rgba(128, 122, 106, 0.06)',
  focalPoint: {
    iconBackgroundColor: monochromeBrighter,
    imageBackgroundColor: 'white',
  },
  outline: paleBlack,
  inputFocusOutlineColor: tintedBlack,
  materialSamples: {
    titleFontWeight: fontWeight.normal,
    descriptionFontWeight: fontWeight.light,
    stockStatusFontWeight: fontWeight.normal,
  },
};

export function ThemeProvider(props: React.PropsWithChildren<{}>) {
  return <GlitzThemeProvider theme={theme}>{props.children}</GlitzThemeProvider>;
}
